import React, { useState } from "react";
import { useAuth, formatErr } from "../contexts/AuthContext";
import { BRAND, LOGO_URL } from "../brand";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { CalendarDays } from "lucide-react";
import { format, parse } from "date-fns";

export default function AgeGate() {
  const { user, login, register: doRegister } = useAuth();
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [dob, setDob] = useState(""); // YYYY-MM-DD for backend
  const [dobOpen, setDobOpen] = useState(false);
  const [confirm21, setConfirm21] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const dobDate = dob ? parse(dob, "yyyy-MM-dd", new Date()) : undefined;
  const today = new Date();
  const minDOB = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
  const maxDOB = today;
  const defaultMonth = dobDate || new Date(today.getFullYear() - 30, today.getMonth(), 1);

  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "register" && !confirm21) {
      setErr("You must confirm you are 21 years of age or older.");
      return;
    }
    if (mode === "register" && !dob) {
      setErr("Please select your date of birth.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") {
        await doRegister({ email, password, dob, name, marketing_opt_in: marketingOptIn });
      } else {
        await login(email, password);
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="age-gate-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto"
      style={{
        background:
          "radial-gradient(800px 500px at 80% -10%, rgba(0,85,255,0.18), transparent 60%), radial-gradient(800px 500px at -10% 100%, rgba(0,195,255,0.12), transparent 60%), #05050A",
      }}
    >
      <div className="grid-lines absolute inset-0 opacity-40 pointer-events-none" />

      {/* Massive blended brand mark behind the form */}
      <img
        src={LOGO_URL}
        alt=""
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[140vw] sm:w-[90vw] max-w-[1100px] h-auto select-none pointer-events-none opacity-90"
        style={{
          mixBlendMode: "screen",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 60% at center, rgba(0,0,0,1) 35%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 85%)",
          maskImage:
            "radial-gradient(ellipse 70% 60% at center, rgba(0,0,0,1) 35%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0) 85%)",
        }}
      />

      <div className="surface w-full max-w-2xl p-8 sm:p-12 relative reveal backdrop-blur-md bg-[#0C0C12]/85">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="overline">{BRAND.tagline}</div>
            <div className="font-heading text-lg tracking-tight mt-1">{BRAND.name}</div>
          </div>
          <div className="hidden sm:block font-mono-tech text-[10px] tracking-[0.2em] text-[#00C3FF]">
            21+ ACCESS REQUIRED
          </div>
        </div>

        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-light leading-tight">
            Restricted Access.<br />
            <span className="text-[#00C3FF]">Research-Grade Compounds.</span>
          </h1>
          <p className="text-sm text-[#8A94A6] mt-4 max-w-md leading-relaxed">
            Verify you are 21 or older and create an account to enter the catalog. All products are
            sold exclusively for research purposes — never for human or animal consumption.
          </p>
        </div>

        <div className="flex gap-6 mb-6 border-b border-white/10">
          <button
            type="button"
            data-testid="tab-register"
            onClick={() => setMode("register")}
            className={`pb-3 text-xs uppercase tracking-[0.2em] font-mono-tech transition-colors ${
              mode === "register" ? "text-[#00C3FF] border-b border-[#00C3FF] -mb-px" : "text-[#5C657A]"
            }`}
          >
            Create Account
          </button>
          <button
            type="button"
            data-testid="tab-login"
            onClick={() => setMode("login")}
            className={`pb-3 text-xs uppercase tracking-[0.2em] font-mono-tech transition-colors ${
              mode === "login" ? "text-[#00C3FF] border-b border-[#00C3FF] -mb-px" : "text-[#5C657A]"
            }`}
          >
            Sign In
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {mode === "register" && (
            <div>
              <label className="overline block mb-2">Full Name</label>
              <input
                data-testid="age-gate-name-input"
                className="input-tech"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dr. Jane Doe"
              />
            </div>
          )}

          <div>
            <label className="overline block mb-2">Email</label>
            <input
              data-testid="age-gate-email-input"
              type="email"
              required
              className="input-tech"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="researcher@lab.com"
            />
          </div>

          <div>
            <label className="overline block mb-2">Password</label>
            <input
              data-testid="age-gate-password-input"
              type="password"
              required
              minLength={8}
              className="input-tech"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
            />
          </div>

          {mode === "register" && (
            <>
              <div>
                <label className="overline block mb-2">Date of Birth</label>
                <Popover open={dobOpen} onOpenChange={setDobOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="age-gate-dob-input"
                      className="input-tech w-full text-left flex items-center justify-between gap-3"
                    >
                      <span className={dobDate ? "text-white" : "text-[#5C657A]"}>
                        {dobDate ? format(dobDate, "MMMM d, yyyy") : "Select your date of birth"}
                      </span>
                      <CalendarDays className="w-4 h-4 text-[#00C3FF] shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    className="z-[110] w-auto p-0 border-white/10 bg-[#0C0C12]/95 backdrop-blur-md text-white"
                    data-testid="age-gate-dob-popover"
                  >
                    <Calendar
                      mode="single"
                      selected={dobDate}
                      defaultMonth={defaultMonth}
                      onSelect={(d) => {
                        if (d) {
                          setDob(format(d, "yyyy-MM-dd"));
                          setDobOpen(false);
                        }
                      }}
                      captionLayout="dropdown"
                      fromDate={minDOB}
                      toDate={maxDOB}
                      disabled={(d) => d > maxDOB || d < minDOB}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input
                  data-testid="age-gate-21-confirm"
                  type="checkbox"
                  checked={confirm21}
                  onChange={(e) => setConfirm21(e.target.checked)}
                  className="mt-1 accent-[#00C3FF]"
                />
                <span className="text-xs text-[#8A94A6] leading-relaxed">
                  I confirm I am 21 years of age or older and acknowledge that all products are sold
                  strictly for <span className="text-[#00C3FF]">research use only</span> — not for
                  human or animal consumption.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="age-gate-marketing-opt-in"
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-1 accent-[#00C3FF]"
                />
                <span className="text-xs text-[#8A94A6] leading-relaxed">
                  Send me product updates, restock alerts, and exclusive offers from ProSource Labs.
                  <span className="text-[#5C657A] block mt-0.5">
                    Optional · unsubscribe anytime.
                  </span>
                </span>
              </label>
            </>
          )}

          {err && (
            <div
              data-testid="age-gate-error"
              className="text-xs font-mono-tech text-red-400 border-l-2 border-red-400 pl-3"
            >
              {err}
            </div>
          )}

          <button
            data-testid="age-gate-submit-button"
            type="submit"
            disabled={busy}
            className="btn-primary w-full justify-center"
          >
            {busy ? "Verifying..." : mode === "register" ? "Enter Catalog" : "Sign In"}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between text-[10px] font-mono-tech tracking-[0.2em] text-[#5C657A] uppercase">
          <span>For Research Use Only</span>
          <span>Not For Human Consumption</span>
        </div>
      </div>
    </div>
  );
}
